import type { UseField } from '@seek/forms-ui';
import { required } from '@seek/validators-js';
import { Dropdown, Notice, Stack, Text, TextField } from 'braid-design-system';
import { useState } from 'react';

import { mandatoryLabel } from '#src/components/MandatoryLabel/MandatoryLabel.tsx';

const possibleNumberOfMaxEvents = new Array(10)
  .fill(1)
  .map((val, i) => val + i);

export interface DeliveryFormData {
  url?: string;
  maxEventsPerAttempt?: string;
}

interface DeliveryFormProps<T extends DeliveryFormData> {
  disabled: boolean;
  mode: 'create' | 'update';
  useField: UseField<T>;
}

export const DeliveryForm = <T extends DeliveryFormData>({
  disabled,
  mode,
  useField,
}: DeliveryFormProps<T>) => {
  const url = useField({ id: 'url', validators: [required] });
  const maxEventsPerAttempt = useField({
    id: 'maxEventsPerAttempt',
    validators: [required],
  });

  const [disruptiveChange, setDisruptiveChange] = useState<boolean>(false);

  return (
    <Stack space="large">
      <TextField
        {...mandatoryLabel('URL')}
        description="The HTTPS URL we can send events to."
        id="webhookSubscriptionUrl"
        disabled={disabled}
        value={url.value ?? ''}
        onChange={(changeEvent) => {
          setDisruptiveChange(true);
          url.onChange(changeEvent);
        }}
        onBlur={url.onBlur}
        tone={url.valid ? 'neutral' : 'critical'}
        message={url.valid ? undefined : 'Required field'}
      />

      <Dropdown
        {...maxEventsPerAttempt}
        {...mandatoryLabel('Max events per request')}
        description="The maximum number of events we’ll include in a single request. You can think of this as the batch size."
        id="webhookSubscriptionMaxEventsPerAttempt"
        placeholder="Select size..."
        tone={maxEventsPerAttempt.valid ? 'neutral' : 'critical'}
        message={maxEventsPerAttempt.valid ? undefined : 'Required field'}
        disabled={disabled}
        value={maxEventsPerAttempt.value ?? ''}
      >
        {possibleNumberOfMaxEvents.map((code) => (
          <option key={code} value={code}>
            {code}
          </option>
        ))}
      </Dropdown>

      {mode === 'update' && disruptiveChange ? (
        <Notice>
          <Text>
            Make sure that your software is ready to receive incoming requests
            with this delivery configuration to avoid disruption.
          </Text>
        </Notice>
      ) : null}
    </Stack>
  );
};
